// Shared "GitHub-like" input surface styling (background/border/focus/error
// states) applied to the `.MuiInputBase-root` of a plain `TextField` below.
// Exported so other MUI input-like components that render their own
// `.MuiInputBase-root` (e.g. a standalone `Select`, via RHFSelect) can reuse
// the exact same rules instead of duplicating these values and risking
// visual drift between TextField/Autocomplete and Select.
// material ui
import { TextFieldProps, Theme } from '@mui/material';

export const inputSurfaceStyles = (theme: Theme) => ({
  padding: '8px 12px',
  fontSize: '14px',
  lineHeight: '20px',
  verticalAlign: 'middle',
  backgroundColor:
    theme.palette.mode === 'dark'
      ? theme.palette.grey[900]
      : theme.palette.grey[100],
  backgroundRepeat: 'no-repeat',
  backgroundPosition: 'right 8px center',
  border:
    theme.palette.mode === 'dark' ? '1px solid #30363d' : '1px solid #e1e4e8',
  borderRadius: '6px',
  outline: 'none',
  boxShadow:
    theme.palette.mode === 'dark'
      ? 'inset 0 1px 0 0 rgba(240, 246, 252, 0.03)'
      : 'inset 0 1px 0 0 rgba(225, 228, 232, 0.2)',
  '& .MuiInputBase-input': {
    padding: 0,
    fontSize: '14px',
    lineHeight: '20px',
    color: theme.palette.mode === 'dark' ? '#c9d1d9' : '#24292e',
  },
  '&.Mui-focused': {
    borderColor: theme.palette.mode === 'dark' ? '#1f6feb' : '#0366d6',
    outline: 'none',
    boxShadow:
      theme.palette.mode === 'dark'
        ? '0 0 0 3px rgba(31, 111, 235, 0.3)'
        : '0 0 0 3px rgba(3, 102, 214, 0.3)',
  },
  '&.Mui-error': {
    borderColor: theme.palette.mode === 'dark' ? '#f85149' : '#d73a49',
    outline: 'none',
    boxShadow:
      theme.palette.mode === 'dark'
        ? '0 0 0 3px rgba(248, 81, 73, 0.3)'
        : '0 0 0 3px rgba(215, 58, 73, 0.3)',
  },
  '& .MuiOutlinedInput-notchedOutline': {
    border: 'none',
  },
});

export const textFieldCustomization = () => ({
  MuiTextField: {
    styleOverrides: {
      root: ({
        ownerState,
        theme,
      }: {
        ownerState: TextFieldProps;
        theme: Theme;
      }) => ({
        '& .MuiInputBase-root': inputSurfaceStyles(theme),
        ...(ownerState.variant === 'filled' && {
          '& .MuiFilledInput-root': {
            '&:before': {
              display: 'none',
            },
            '&:after': {
              display: 'none',
            },
          },
        }),
        // Label while inside the compact input.
        '& .MuiInputLabel-root': {
          transform: 'translate(12px, 7px) scale(1)',
        },

        // Label after focus or when the field has a value.
        '& .MuiInputLabel-root.MuiInputLabel-shrink': {
          transform: 'translate(12px, -17px) scale(0.75)',
        },

        ...(ownerState.variant === 'filled' && {
          '& .MuiFilledInput-root': {
            '&:before, &:after': {
              display: 'none',
            },
          },
        }),
      }),
    },
  },
});
